import { FC, useState } from 'react';

import CtaButton from '@components/quiz/shared/CtaButton';
import StickyCtaBar from '@components/quiz/shared/StickyCtaBar';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import type { SanityReadinessTemplateTeamBlock } from '@lib/sanity/types';

type TemplateTeamBlockProps = SanityReadinessTemplateTeamBlock & {
  onBuilt: () => void;
  onBack: () => void;
};

/**
 * Step 2b: one-tap template squad picker for users with no FPL team.
 * The three squads (title, description, 15 OPTA IDs) are Sanity-editable.
 */
const TemplateTeamBlock: FC<TemplateTeamBlockProps> = ({
  templateSquads,
  backLinkText,
  ctaText,
  onBuilt,
  onBack
}) => {
  const { state, actions } = useReadiness();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const selectedSquad = templateSquads?.find(
    (squad) => squad._key === selectedKey
  );

  const handleBuild = async () => {
    if (!selectedSquad || state.isImporting) return;
    const isBuilt = await actions.setTeamFromTemplate(
      selectedSquad.optaPlayerIds
    );
    if (isBuilt) onBuilt();
  };

  return (
    <section className="flex flex-col items-center px-6 pb-28 pt-8">
      <div className="w-full max-w-sm space-y-3">
        {templateSquads?.map((squad) => {
          const isSelected = squad._key === selectedKey;

          return (
            <button
              key={squad._key}
              type="button"
              onClick={() => setSelectedKey(squad._key)}
              data-cy={`readiness-template-squad-${squad._key}`}
              className={`flex w-full items-center gap-3 rounded-xl border px-5 py-4 text-left transition-all ${
                isSelected
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-neutral-200 bg-white'
              }`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                  isSelected
                    ? 'border-primary-600 bg-primary-600'
                    : 'border-neutral-300'
                }`}
              >
                {isSelected && (
                  <span className="h-2 w-2 rounded-full bg-white" />
                )}
              </span>
              <span>
                <span className="block font-heading text-sm font-bold text-neutral-800">
                  {squad.title}
                </span>
                {squad.description && (
                  <span className="block font-body text-xs text-neutral-500">
                    {squad.description}
                  </span>
                )}
              </span>
            </button>
          );
        })}

        {state.importError && (
          <p className="font-body text-sm text-danger-600">
            {state.importError}
          </p>
        )}
      </div>

      <StickyCtaBar>
        <CtaButton
          onClick={handleBuild}
          disabled={!selectedSquad || state.isImporting}
          dataCy="readiness-build-squad-cta"
        >
          {state.isImporting
            ? 'Building your squad…'
            : ctaText ?? 'Build my squad'}
        </CtaButton>
        <button
          type="button"
          onClick={onBack}
          data-cy="readiness-template-back-link"
          className="mt-3 w-full text-center font-body text-sm font-semibold text-primary-500 transition-colors hover:text-primary-600"
        >
          {backLinkText ?? 'I do have a team, take me back'}
        </button>
      </StickyCtaBar>
    </section>
  );
};

export default TemplateTeamBlock;
